import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ThumbsUp, ThumbsDown, Send, X } from 'lucide-react';
import { QueryPlan } from './QueryPlan';
import { QueryAgentCard } from './QueryAgentCard';
import { QuerySummary } from './QuerySummary';
import { HandoffMessage } from './HandoffMessage';
import { createQueryWorkflow } from '../../data/queryWorkflowData';

export const QueryResponseUI = ({ 
  userQuestion, 
  timestamp,
  isStreaming = false,
  onComplete,
  onPublish
}) => {
  const [workflow, setWorkflow] = useState(null);
  
  // Simple sequential state - which items are visible
  const [showPlan, setShowPlan] = useState(false);
  const [planDone, setPlanDone] = useState(false);
  const [visibleAgents, setVisibleAgents] = useState(0);
  const [visibleHandoffs, setVisibleHandoffs] = useState(0);
  const [agentsDone, setAgentsDone] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [allComplete, setAllComplete] = useState(false);
  
  // Track expanded state for handoff message visibility
  const [planExpanded, setPlanExpanded] = useState(true);
  const [agentExpanded, setAgentExpanded] = useState({});
  
  // Feedback state
  const [feedback, setFeedback] = useState(null); // 'like' | 'dislike' | null
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  
  const initialized = useRef(false);
  const containerRef = useRef(null);
  const endRef = useRef(null);

  // Handle feedback button click
  const handleFeedbackClick = (type) => {
    if (feedback === type) {
      // Clicking same button again - deselect
      setFeedback(null);
      setShowFeedbackInput(false);
    } else {
      setFeedback(type);
      setShowFeedbackInput(true);
      setFeedbackSubmitted(false);
    }
  };

  // Handle feedback submission
  const handleFeedbackSubmit = () => {
    // Here you would send feedback to backend
    console.log('Feedback submitted:', { type: feedback, text: feedbackText });
    setFeedbackSubmitted(true);
    setShowFeedbackInput(false);
    // Keep the feedback selection visible but close the input
  };

  // Handle closing feedback input without submitting
  const handleFeedbackClose = () => {
    setShowFeedbackInput(false);
    // Keep the feedback selection
  };

  // Auto-scroll to bottom when content changes
  const scrollToBottom = () => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    
    const workflowData = createQueryWorkflow(userQuestion);
    setWorkflow(workflowData);

    // Start showing plan
    setTimeout(() => setShowPlan(true), 300);
  }, [userQuestion]);

  // Scroll when new content appears
  useEffect(() => {
    scrollToBottom();
  }, [showPlan, planDone, visibleAgents, showSummary]);

  // Set allComplete after summary appears
  useEffect(() => {
    if (showSummary) {
      // Small delay to let summary render, then collapse all
      setTimeout(() => setAllComplete(true), 500);
    }
  }, [showSummary]);

  // Update expanded states when allComplete changes
  useEffect(() => {
    if (allComplete) {
      setPlanExpanded(false);
      const collapsedAgents = {};
      workflow?.subAgents.forEach((_, idx) => {
        collapsedAgents[idx] = false;
      });
      setAgentExpanded(collapsedAgents);
    }
  }, [allComplete, workflow]);

  const handlePlanComplete = () => {
    setPlanDone(true);
    // Show first handoff message
    setTimeout(() => {
      setVisibleHandoffs(1);
      scrollToBottom();
      // Then show first agent after delay
      setTimeout(() => {
        setVisibleAgents(1);
        scrollToBottom();
      }, 800);
    }, 200);
  };

  const handleAgentComplete = (agentIndex) => {
    setAgentsDone(prev => [...prev, agentIndex]);
    
    // Show next handoff or summary
    setTimeout(() => {
      if (workflow && agentIndex + 1 < workflow.subAgents.length) {
        // Show handoff message first
        setVisibleHandoffs(agentIndex + 2);
        scrollToBottom();
        // Then show agent after delay
        setTimeout(() => {
          setVisibleAgents(agentIndex + 2);
          scrollToBottom();
        }, 800);
      } else {
        setShowSummary(true);
        if (onComplete) onComplete();
      }
      scrollToBottom();
    }, 200);
  };

  // Callback for when a step changes within an agent (for scrolling)
  const handleStepChange = () => {
    scrollToBottom();
  };

  // Track plan expanded state
  const handlePlanExpandChange = (expanded) => {
    setPlanExpanded(expanded);
  };

  // Track agent expanded state
  const handleAgentExpandChange = (idx, expanded) => {
    setAgentExpanded(prev => ({ ...prev, [idx]: expanded }));
  };

  // Determine if handoff message should be visible
  // Visible when either preceding or following card is expanded
  const isHandoffVisible = (agentIndex) => {
    if (agentIndex === 0) {
      // First handoff: between Plan and first agent
      return planExpanded || agentExpanded[0] !== false;
    }
    // Subsequent handoffs: between agents
    return agentExpanded[agentIndex - 1] !== false || agentExpanded[agentIndex] !== false;
  };

  if (!workflow) return null;

  return (
    <div className="w-full" ref={containerRef} data-testid="query-response-ui">
      {/* Plan */}
      {showPlan && (
        <QueryPlan 
          plan={workflow.plan}
          onComplete={handlePlanComplete}
          onStepChange={handleStepChange}
          forceCollapse={allComplete}
          onExpandChange={handlePlanExpandChange}
        />
      )}

      {/* Agents with Handoff Messages */}
      {workflow.subAgents.map((agent, idx) => (
        <React.Fragment key={agent.id}>
          {/* Handoff message before each agent - appears first */}
          {idx < visibleHandoffs && agent.handoffMessage && (
            <HandoffMessage 
              message={agent.handoffMessage}
              isVisible={isHandoffVisible(idx)}
            />
          )}
          
          {/* Agent card - appears after handoff */}
          {idx < visibleAgents && (
            <QueryAgentCard
              subAgent={agent}
              isActive={!agentsDone.includes(idx)}
              onComplete={() => handleAgentComplete(idx)}
              onStepChange={handleStepChange}
              forceCollapse={allComplete}
              onExpandChange={(expanded) => handleAgentExpandChange(idx, expanded)}
              onPublish={onPublish}
            />
          )}
        </React.Fragment>
      ))}

      {/* Summary */}
      {showSummary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <QuerySummary
            summary={workflow.summary}
            metadata={workflow.metadata}
          />
          
          <div className="flex items-center justify-between text-[10px] text-gray-600 px-1 py-0.5 mt-0.5">
            <span className="flex items-center gap-3">
              <span>{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
              <span className="text-gray-700">|</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                Total: {workflow.metadata.totalDuration}
              </span>
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleFeedbackClick('like')}
                className={`p-1.5 rounded transition-colors ${
                  feedback === 'like' 
                    ? 'text-green-500' 
                    : 'text-gray-600 hover:text-green-400'
                }`}
                data-testid="feedback-like-btn"
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleFeedbackClick('dislike')}
                className={`p-1.5 rounded transition-colors ${
                  feedback === 'dislike' 
                    ? 'text-red-500' 
                    : 'text-gray-600 hover:text-red-400'
                }`}
                data-testid="feedback-dislike-btn"
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          {/* Feedback Text Input */}
          <AnimatePresence>
            {showFeedbackInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-2 rounded-lg bg-slate-800/30 border border-slate-700/30 p-3 focus-within:border-blue-500/50 transition-colors">
                  <div className="flex items-start gap-2">
                    <textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder={feedback === 'like' ? "What did you like about this response?" : "How can we improve this response?"}
                      className="flex-1 bg-transparent border-0 text-xs text-gray-300 placeholder-gray-500 resize-none focus:outline-none focus:ring-0"
                      rows={2}
                      data-testid="feedback-text-input"
                    />
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleFeedbackSubmit}
                        className="p-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white transition-colors"
                        data-testid="feedback-submit-btn"
                      >
                        <Send className="w-3 h-3" />
                      </button>
                      <button
                        onClick={handleFeedbackClose}
                        className="p-1.5 rounded-md bg-slate-700/50 hover:bg-slate-600/50 text-gray-400 hover:text-gray-300 transition-colors"
                        data-testid="feedback-close-btn"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Feedback Submitted Confirmation */}
          <AnimatePresence>
            {feedbackSubmitted && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mt-1"
              >
                <p className="text-[10px] text-gray-500 text-right">Thanks for your feedback!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      
      {/* Scroll anchor with breathing space - only during execution */}
      <div ref={endRef} className={showSummary ? "h-1" : "h-32"} />
    </div>
  );
};

export default QueryResponseUI;
